import { useMemo, useState } from 'react'
import { Box, Grid2 as Grid, useTheme } from '@mui/material'
import { DateRangeValue, GroupOption } from '@/types/metrics'
import {
  AverageCompletionTimeMetricReport,
  AverageMetricReport,
  ByTimeMetricReport,
  CountDateMetricReport,
  DocCompletedOverallMetricReport,
  OrgWorkflowMetricReportOverallStp,
} from '@/types/workflow-metric-reports'
import useMetricsSearchParams from '@/hooks/useMetricsSearchParams'
import { useGetBillingEventMetrics } from '@/service-library/hooks/billing-events'
import { useGetMetricReport } from '@/service-library/hooks/metrics'
import { getLeftMarginForCountChart } from '@/utils/chart'
import { convertRangeValueToDates } from '@/utils/date-metrics'
import TimeCountBarChart from '@/components/charts/TimeCountBarChart'
import { useRootOrganization } from '@/components/organizations/RootOrganizationProvider'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import GeneralInfoCards from './cards/GeneralInfoCards'
import AverageHumanInteractionCard from './cards/AverageHumanInteractionCard'
import ByTimeAndStateMetricLineCard from './cards/ByTimeAndStateMetricLineCard'
import CompletedDocumentsCountCard from './cards/CompletedDocumentsCountCard'
import IdleDocumentsCountCard from './cards/IdleDocumentsCountCard'
import DocumentsCountChart from './cards/DocumentsCountChart'
import { CommonQueryOptions } from './types'
import AddedAndCompletedLineChart from './AddedAndCompletedLineChart'
import AverageMetricBarChart from './AverageMetricBarChart'
import ChartCard from './ChartCard'
import MetricFiltersBar from './MetricFiltersBar'

export type DateRange = {
  startDate: Date
  endDate: Date
}

const tooltipMap = {
  stp: (
    <>
      The percentage of documents that enter and then leave the workflow state
      without stopping.
      <br />
      <br />
      Keep in mind that a document that is successfully straight-through
      processed in one workflow state may still stop and become idle in another.
      These numbers represent the success rate for each individual workflow
      state.
    </>
  ),
  averageStateDuration: (
    <>
      The average time documents spend in each workflow state.
      <br />
      <br />
      If a document automatically exits a state immediately after entering, it
      is considered &quot;straight-through processed&quot; and does not count
      toward the values on this chart. Only documents that spend time idle in a
      state are considered.
      <br />
      <br />
      We would have made the title of this chart &quot;how long on average
      documents get stuck in a workflow state&quot; but that was too long.
    </>
  ),
  docStpCount:
    'The number of documents that were straight-through processed in each workflow state over time.',
  overallStpCount: `The overall number of documents straight-through processed over time. These numbers represent documents that were added to 
    the project and then moved through all relevant workflow states to a "Done" workflow state without going idle. If a document 
    stopped in any "Intake" or "In Progress" workflow state, it is not counted in these numbers.`,
  addedDocumentsCount: 'The number of documents added to the workflow.',
}

export default function ProjectMetricsDashboard() {
  const theme = useTheme()
  const { rootOrganization } = useRootOrganization()
  const { selectedWorkflow } = useSelectedWorkflowContext()

  const { values, updateValues } = useMetricsSearchParams()
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(
    values.org_id || rootOrganization.id,
  )
  const [selectedDateRangeValue, setSelectedDateRangeValue] =
    useState<DateRangeValue>((values.range_value as DateRangeValue) || '1week')

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate:
      values.range_value === 'custom'
        ? new Date(values.from)
        : convertRangeValueToDates(selectedDateRangeValue).startDate,
    endDate:
      values.range_value === 'custom'
        ? new Date(values.to)
        : convertRangeValueToDates(selectedDateRangeValue).endDate,
  })

  const [groupBy, setGroupBy] = useState<GroupOption>(
    (values.grouped_by as GroupOption) || 'day',
  )

  const commonQueryOptions: CommonQueryOptions = {
    filters: {
      org_and_descendants_for_id: selectedOrganizationId,
      workflow_id: selectedWorkflow.id,
      period_started_at__range: `${selectedDateRange.startDate.toISOString()},${selectedDateRange.endDate.toISOString()}`,
    },
    enabled: !!selectedWorkflow && !!selectedOrganizationId,
  }

  const { billingEventMetrics } = useGetBillingEventMetrics({
    startDate: selectedDateRange.startDate,
    endDate: selectedDateRange.endDate,
    groupedBy: groupBy,
    tz: 'America/Denver', // Since all other metrics come in MST time, we hard code it here
    filters: {
      org_and_descendants_for_id: selectedOrganizationId,
      workflow_id: selectedWorkflow.id,
    },
  })

  const { report: overallSTPReport } =
    useGetMetricReport<OrgWorkflowMetricReportOverallStp>({
      ...commonQueryOptions,
      reportKey: 'average-overall-stp',
    })

  const { report: docSTPAverageReport } =
    useGetMetricReport<AverageMetricReport>({
      ...commonQueryOptions,
      reportKey: 'doc-stp-average',
    })

  const { report: overallDocCompleteCount } =
    useGetMetricReport<DocCompletedOverallMetricReport>({
      ...commonQueryOptions,
      reportKey: 'overall-doc-completed-count',
    })

  const { report: averageDocCompleteTime } =
    useGetMetricReport<AverageCompletionTimeMetricReport>({
      ...commonQueryOptions,
      reportKey: 'average-overall-doc-completed-time',
    })

  const commonOptionsWithGroupBy = {
    ...commonQueryOptions,
    filters: {
      ...commonQueryOptions.filters,
      grouped_by: groupBy,
    },
  }

  const { report: docCompleteCount } = useGetMetricReport<ByTimeMetricReport>({
    ...commonOptionsWithGroupBy,
    reportKey: 'doc-completed-count',
  })

  const { report: overallStpCount } = useGetMetricReport<CountDateMetricReport>(
    {
      ...commonOptionsWithGroupBy,
      reportKey: 'overall-stp-count',
    },
  )

  const maxDocCountYValue = Math.max(
    ...(billingEventMetrics?.results.map((r) => r.doc_count) || []),
  )

  const addedDocumentsReport = useMemo(
    () =>
      billingEventMetrics
        ? { reports: billingEventMetrics.results }
        : undefined,
    [billingEventMetrics],
  )

  return (
    <Box sx={{ p: 4, width: '100%', maxWidth: 2000, m: '0 auto' }}>
      <MetricFiltersBar
        groupBy={groupBy}
        selectedDateRange={selectedDateRange}
        selectedDateRangeValue={selectedDateRangeValue}
        selectedOrganizationId={selectedOrganizationId}
        handleUpdateDateRangeData={(
          value: DateRangeValue,
          dateRange: DateRange,
        ) => {
          setSelectedDateRange(dateRange)
          setSelectedDateRangeValue(value)
          updateValues({
            range_value: value,
            from: value === 'custom' ? dateRange.startDate.toISOString() : '',
            to: value === 'custom' ? dateRange.endDate.toISOString() : '',
          })
        }}
        handleUpdateGroupBy={(groupedBy) => {
          setGroupBy(groupedBy)
          updateValues({ grouped_by: groupedBy })
        }}
        handleUpdateOrganizationId={(orgId) => {
          setSelectedOrganizationId(orgId)
          updateValues({ org_id: orgId })
        }}
      />

      <Grid container spacing={1}>
        <GeneralInfoCards
          addedDocumentsMetrics={billingEventMetrics}
          completedDocumentsMetrics={{
            count: overallDocCompleteCount?.doc_completed_overall || 0,
            avg_time_to_completion:
              averageDocCompleteTime?.doc_completed_time_average_overall || 0,
            overall_stp: overallSTPReport?.doc_stp_average_overall || 0,
          }}
        />

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            heading="Added Documents"
            headingTooltip={tooltipMap.addedDocumentsCount}
          >
            <TimeCountBarChart
              enableLabel
              height={250}
              labelSkipHeight={12}
              labelSkipWidth={20}
              from={selectedDateRange.startDate}
              to={selectedDateRange.endDate}
              data={billingEventMetrics?.results || []}
              colors={theme.palette.primary.main}
              groupedBy={groupBy}
              margin={{
                left: getLeftMarginForCountChart(maxDocCountYValue),
                right: 20,
              }}
              yKey="doc_count"
            />
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CompletedDocumentsCountCard
            report={docCompleteCount}
            groupBy={groupBy}
            selectedDateRange={selectedDateRange}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ByTimeAndStateMetricLineCard
            heading="Average State Duration"
            reportKey="doc-state-duration"
            commonQueryOptions={commonOptionsWithGroupBy}
            selectedDateRange={selectedDateRange}
            type="time"
            headingTooltip={tooltipMap.averageStateDuration}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <IdleDocumentsCountCard
            commonQueryOptions={commonOptionsWithGroupBy}
            selectedDateRange={selectedDateRange}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            heading="Overall STP Document Count"
            headingTooltip={tooltipMap.overallStpCount}
            controls={<Box sx={{ height: 30 }} />} // adding to match height of the STP Document Count card that includes an IconButton
          >
            <DocumentsCountChart
              groupedBy={groupBy}
              selectedDateRange={selectedDateRange}
              report={overallStpCount}
            />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ByTimeAndStateMetricLineCard
            heading="STP Document Count"
            reportKey="doc-stp-count"
            commonQueryOptions={commonOptionsWithGroupBy}
            selectedDateRange={selectedDateRange}
            type="count"
            headingTooltip={tooltipMap.docStpCount}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            heading="Straight-Through Processing Rate"
            headingTooltip={tooltipMap.stp}
            controls={<Box sx={{ height: 40 }} />} // adding to match height of AverageHumanInteractionCard that includes an Autocomplete field
          >
            <AverageMetricBarChart
              report={docSTPAverageReport}
              type="doc-stp-average"
            />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <AverageHumanInteractionCard
            commonQueryOptions={commonQueryOptions}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard heading="Added vs Completed">
            <AddedAndCompletedLineChart
              groupBy={groupBy}
              selectedDateRange={selectedDateRange}
              addedReport={addedDocumentsReport}
              completedReport={docCompleteCount}
            />
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  )
}
